import { Pipe, PipeTransform } from '@angular/core';
import type { LocationSearchResult } from '@sal/location/application/ports';

import { firstBy } from 'thenby';
import { SuggestContentType } from '../../enums/suggest-content-type.enum';

@Pipe({ name: 'sortSearchResults' })
export class SortSearchResultsPipe implements PipeTransform {
    /**
     * Excepts an Array with scheme { 'suggestContentType': SuggestContentType, 'grpInfo': object[] }.
     * @param  objects        Array containing an object with the SuggestContentType and an array of sortable objects
     * @param  sortProperties properties to sort array
     * @return                sorted array
     */
    transform(objects: any[], sortProperties?: any[]): object[] {
        let items, sorted;
        objects.map(item => {
            if (sortProperties === undefined) {
                items = item.grpInfo || [];
                sorted = this.sort(items, item.suggestContentType);
            } else {
                sorted = this.sortBy(objects, sortProperties);
            }
        });
        return objects;
    }

    sort(arr: any[], type: string): any[] {
        switch (type) {
            case SuggestContentType.Location:
                arr = this.sortLocationDefault(arr);
                break;
            case SuggestContentType.Country:
                arr = arr.sort(this.getParsedComparatorByKey('CountryName'));
                break;
            default:
                break;
        }
        return arr;
    }

    sortBy(arr: any[], properties: any[]): any[] {
        if (properties.length <= 0) {
            return arr;
        } else {
            let fn = firstBy(properties[0]);
            properties.map((prop, i) => {
                if (i > 0) {
                    fn = fn.thenBy(prop);
                }
            });
            return arr.sort(fn);
        }
    }

    sortLocationDefault(arr: LocationSearchResult[]): LocationSearchResult[] {
        return arr.sort((e1, e2) => {
            const compareNameHQ = this.CompanyNameCompare(e1.company?.name, e2.company?.name);
            const compareHQ = this.HQCompare(e1.company?.isHeadquarters, e2.company?.isHeadquarters);
            const compareLocCode = this.localeCompare(e1.locationCode, e2.locationCode);
            const compareStreet = this.localeCompare(e1.address?.street, e2.address?.street);

            return compareHQ || compareNameHQ || compareLocCode || compareStreet || 0;
        });
    }

    CompanyNameCompare(v1: string | undefined, v2: string | undefined): number {
        if (v1 !== 'Siemens AG') {
            return 1;
        }
        if (v2 !== 'Siemens AG') {
            return -1;
        }
        return 0;
    }

    HQCompare(v1: boolean | undefined, v2: boolean | undefined): number {
        if (!v1 && v2) {
            return 1;
        }
        if (v1 && !v2) {
            return -1;
        }
        return 0;
    }

    localeCompare(v1: string | undefined, v2: string | undefined): number {
        if (v1 === undefined) {
            return 1;
        }
        if (v2 === undefined) {
            return -1;
        }
        //  console.log(v1);
        return v1.localeCompare(v2);
    }

    parse(input: any[] | undefined): any {
        if (input === undefined) {
            return {};
        } else if (typeof input === 'string') {
            return JSON.parse(input || '{}');
        } else if (Array.isArray(input)) {
            return input.map((elem: any) => {
                return this.parse(elem.value || '');
            });
        } else if (typeof input === 'object') {
            // if input is already an object there is no need to parse,
            // so it is returned
            return input;
        } else {
            return {};
        }
    }

    toObj(arr: any[], key?: string): { [key: string]: any } {
        if (key === undefined) {
            key = 'SALID';
        }
        const obj: { [key: string]: any } = {};
        let id;
        arr.map((elem: any) => {
            id = elem[key];
            if (id !== undefined) {
                obj[id] = elem;
            }
        });
        return obj;
    }

    getVal(obj: { [key: string]: any }, key: string): any {
        return obj[key] || '';
    }

    getParsedComparatorByKey(key: string, fn?: (a: any, b: any) => number): (a: any, b: any) => number {
        if (fn === undefined) {
            fn = (a, b) => {
                return this.localeCompare(a, b);
            };
        }
        return (a: any, b: any) => {
            return fn(a[key], b[key]);
        };
    }
}
